/**
 * CallLandingPage — /calls/:callSlug
 *
 * Public landing page for a recurring TSB call.
 * Fetches call data + next occurrence from GET /api/public/calls/:slug.
 * Zoom join link is time-gated: only shown within 15 min of the call start.
 * Before that window, a live countdown shows "Doors open in X h Y m Z s".
 */

import { useEffect, useState, useRef, type FormEvent, type CSSProperties } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import TakerShell from './components/TakerShell';
import { CountrySelect } from './components/CountrySelect';
import { SmsOptIn } from './components/SmsOptIn';
import { NewsletterOptIn } from './components/NewsletterOptIn';
import { offersNewsletterOnCall } from './lib/newsletterOptIn';
import { suggestTypoCorrection } from './lib/email-validator';
import { fetchWithRetry } from './lib/fetchRetry';
import { getAttributionPayload } from './attribution';
import { recordLandingView } from './api';
import { callWhenTitle, type UpcomingDate } from './lib/callWhen';
import CallPageAskBox from './components/CallPageAskBox';
import { useBrandGtm } from './lib/useBrandGtm';
import { splitCallSearch } from './lib/callPrivacy';

const API_BASE = (import.meta as unknown as { env: Record<string, string> }).env?.VITE_API_BASE_URL ?? '';

type DoorIdentity = { firstName: string | null; email: string };

/**
 * Door email card (attendance identity gate, Phase 2). Shown when the doors are
 * open and we don't already know the visitor (no personal-link token, no device
 * memory). They enter the email their membership is under; the engine resolves
 * it (direct + HubSpot secondary-email). Recognized → hand back to the confirm
 * card. Not recognized → a gentle retry, then "join anyway" as a guest (which
 * records + Slack-logs server-side). A quiet "just join" link always lets
 * someone in without identifying — the gate records, it never blocks entry.
 */
function DoorEmailCard({ callSlug, joinUrl, joinBtnStyle, onRecognized }: {
  callSlug: string;
  joinUrl: string;
  joinBtnStyle: CSSProperties;
  onRecognized: (id: DoorIdentity) => void;
}) {
  const [email, setEmail] = useState('');
  const [phase, setPhase] = useState<'entry' | 'retry'>('entry');
  const [busy, setBusy] = useState(false);
  const suggestion = suggestTypoCorrection(email);

  const openZoom = () => window.open(joinUrl, '_blank', 'noopener,noreferrer');

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const clean = email.trim().toLowerCase();
    if (!clean.includes('@')) return;
    setBusy(true);
    try {
      const r = await fetch(`${API_BASE}/api/public/calls/${encodeURIComponent(callSlug)}/checkin`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: clean }),
      });
      const d = await r.json().catch(() => ({})) as { recognized?: boolean; firstName?: string | null };
      if (d.recognized) { onRecognized({ firstName: d.firstName ?? null, email: clean }); return; }
      setPhase('retry');
    } catch {
      // Fail-open: if resolution errors, let them straight in.
      openZoom();
    } finally {
      setBusy(false);
    }
  };

  // Guest admit — record + Slack-log server-side, then enter regardless.
  const joinAsGuest = () => {
    const clean = email.trim().toLowerCase();
    if (clean.includes('@')) {
      fetch(`${API_BASE}/api/public/calls/${encodeURIComponent(callSlug)}/checkin`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: clean, guest: true }), keepalive: true,
      }).catch(() => { /* the gate records; it never blocks entry */ });
    }
    openZoom();
  };

  const inputStyle: CSSProperties = {
    width: '100%', padding: '11px 13px', borderRadius: 8, fontSize: 14,
    border: '1px solid rgba(255,255,255,0.18)', background: 'rgba(255,255,255,0.08)',
    color: '#fff', outline: 'none', boxSizing: 'border-box',
  };
  const linkStyle: CSSProperties = {
    background: 'none', border: 'none', color: '#9ca3af', fontSize: 12.5,
    cursor: 'pointer', textDecoration: 'underline', padding: 0,
  };

  return (
    <div style={{
      display: 'inline-flex', flexDirection: 'column', alignItems: 'stretch', gap: 10,
      background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.14)',
      borderRadius: 12, padding: '18px 22px', maxWidth: 380, width: '100%', textAlign: 'left',
    }}>
      <div style={{ fontSize: 13.5, color: '#e5e7eb', textAlign: 'center', lineHeight: 1.5 }}>
        {phase === 'entry'
          ? 'So we know who’s joining, what’s the email your membership is under?'
          : 'We don’t see that email on the list — is there another one you might use?'}
      </div>
      <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
          placeholder="you@yourfirm.com" autoComplete="email" style={inputStyle} required />
        {suggestion && (
          <button type="button" onClick={() => setEmail(suggestion)}
            style={{ ...linkStyle, textAlign: 'left', color: '#F8A07A' }}>
            Did you mean {suggestion}?
          </button>
        )}
        <button type="submit" disabled={busy}
          style={{ ...joinBtnStyle, border: 'none', cursor: busy ? 'wait' : 'pointer', opacity: busy ? 0.6 : 1 }}>
          {busy ? '…' : phase === 'entry' ? 'Continue' : 'Try this email'}
        </button>
      </form>
      {phase === 'retry' && (
        <button type="button" onClick={joinAsGuest} style={{ ...linkStyle, textAlign: 'center' }}>
          Join anyway
        </button>
      )}
      <button type="button" onClick={openZoom} style={{ ...linkStyle, textAlign: 'center', color: '#6b7280' }}>
        Having trouble? Just join
      </button>
    </div>
  );
}

/** Minutes before start_utc at which the Zoom button becomes visible. */
const DOOR_OPEN_MINUTES = 15;

/** Two People fields can carry near-identical copy (a role used as a tagline,
 *  or vice versa) — suppress the second line when either contains the other. */
function textsOverlap(a: string, b: string): boolean {
  const norm = (s: string) => s.toLowerCase().replace(/\s+/g, ' ').trim();
  const na = norm(a);
  const nb = norm(b);
  return na.includes(nb) || nb.includes(na);
}

interface CallHost {
  name: string;
  /** The person's title from the People directory (people.role), e.g. "CEO, The Successful Bookkeeper Global". */
  role?: string | null;
  tagline: string | null;
  photo_url: string | null;
}

/** EE-owned landing narrative (calls.landing_json) — story, agenda, closing. */
interface CallLanding {
  why_title?: string;
  why_lead?: string;
  why_highlight?: string[];
  why_close?: string;
  agenda_title?: string;
  agenda?: Array<{ title: string; body: string }>;
  closing_title?: string;
  closing_body?: string;
  /** Names who the call is for when the access sources can't, e.g. "Summit 2026 VIPs and Member Pro"
   *  for a call that admits named guests as well as a tier. EE-owned (landing_json). */
  audience_label?: string;
}

interface CallData {
  slug: string;
  brand: string;
  name: string;
  display_name: string | null;
  tagline: string | null;
  blurb: string | null;
  host: string | null;
  host_photo_url: string | null;
  host_tagline: string | null;
  hosts?: CallHost[];
  landing?: CallLanding | null;
  cadence: string | null;
  /** Set when the series has a fixed last date — the When card then lists upcoming dates. */
  series_end_date?: string | null;
  /** One session (CC freq='once'), not a series: no recurring coaching-call framing. */
  one_off?: boolean;
  description: string | null;
  duration_minutes: number;
  audience_type: string;
  audience_ref: string | null;
  /** Registration-gated call (town hall): show the RSVP form. */
  rsvp_open?: boolean;
  /** Plain-words audience derived from the call's active access sources. */
  audience_label?: string | null;
  /** Call admits only people granted by name: no "Register here" pitch. */
  invite_only?: boolean;
  /** 'members' when the open sign-up does not get the replay (worker
   *  calls-source-flags.ts): the page then says the recording goes to Member
   *  Plus and Pro. Absent or 'everyone' keeps the "everyone" wording. */
  recording_for?: 'everyone' | 'members' | 'window';
  /** With recording_for 'window' (0407): sign-ups get the recording free for
   *  this many days after the replay is released, then it moves to Member Plus and Pro. */
  recording_window_days?: number | null;
  /** Where the "Not a member yet?" card sends a visitor — the call's own link
   *  or the brand's default (Member Plus gates for TSB). Null = keep the
   *  old website link. */
  register_url?: string | null;
  /** The yearly link shown beneath the monthly one. */
  register_url_annual?: string | null;
}

interface NextOccurrence {
  occurrence_date: string;
  start_utc: string;
  timezone: string;
  duration_minutes: number;
  status: string;
  zoom_join_url: string | null;
  /** True when a Zoom link exists for this occurrence, even before the door
   *  window (the raw URL itself is withheld until doors open). Drives the
   *  countdown / join affordance without leaking the URL early. */
  zoom_ready?: boolean;
}

type PageState =
  | { phase: 'loading' }
  | { phase: 'error'; message: string }
  | { phase: 'ready'; call: CallData; next: NextOccurrence | null; upcoming: UpcomingDate[] };

function formatDateTime(startUtc: string, timezone: string, durationMin: number): {
  dayLong: string; timeRange: string; timeShort: string;
} {
  const d = new Date(startUtc);
  const tz = timezone || 'America/Toronto';
  const opts: Intl.DateTimeFormatOptions = { timeZone: tz };
  const dayLong = new Intl.DateTimeFormat('en-US', { ...opts, weekday: 'long', month: 'long', day: 'numeric' }).format(d);
  const timeShort = new Intl.DateTimeFormat('en-US', { ...opts, hour: 'numeric', minute: '2-digit', timeZoneName: 'short' }).format(d);
  const endMs = d.getTime() + durationMin * 60_000;
  const endTime = new Intl.DateTimeFormat('en-US', { ...opts, hour: 'numeric', minute: '2-digit', timeZoneName: 'short' }).format(new Date(endMs));
  return { dayLong, timeShort, timeRange: `${timeShort} – ${endTime}` };
}

/** "For you, that's Wednesday, June 17 at 7:00 AM AEST" — computed in the
 *  visitor's own time zone. Null when it would just repeat the call-zone time. */
function formatVisitorLocal(startUtc: string, callTimezone: string): string | null {
  try {
    const visitorTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (!visitorTz || visitorTz === callTimezone) return null;
    const d = new Date(startUtc);
    const stamp = (tz: string) => new Intl.DateTimeFormat('en-US', {
      timeZone: tz, weekday: 'short', hour: 'numeric', minute: '2-digit',
    }).format(d);
    // Zones that share the wall-clock AND the day (e.g. Toronto vs New York) — skip the echo.
    if (stamp(visitorTz) === stamp(callTimezone)) return null;
    return new Intl.DateTimeFormat('en-US', {
      timeZone: visitorTz, weekday: 'long', month: 'long', day: 'numeric',
      hour: 'numeric', minute: '2-digit', timeZoneName: 'short',
    }).format(d);
  } catch {
    return null;
  }
}

/** Returns seconds until (start_utc minus DOOR_OPEN_MINUTES). Negative = doors already open. */
function secondsUntilDoor(startUtc: string): number {
  const doorMs = new Date(startUtc).getTime() - DOOR_OPEN_MINUTES * 60_000;
  return Math.floor((doorMs - Date.now()) / 1000);
}

function formatCountdown(secs: number): string {
  if (secs <= 0) return '';
  const d = Math.floor(secs / 86400);
  const h = Math.floor((secs % 86400) / 3600);
  const m = Math.floor((secs % 3600) / 60);
  const s = secs % 60;
  if (d > 0) return `${d}d ${h}h ${m}m ${String(s).padStart(2, '0')}s`;
  if (h > 0) return `${h}h ${m}m ${String(s).padStart(2, '0')}s`;
  if (m > 0) return `${m}m ${String(s).padStart(2, '0')}s`;
  return `${s}s`;
}

/** Live countdown hook. Returns seconds remaining until the door opens (negative once open). */
function useCountdown(startUtc: string | null): number {
  const [secs, setSecs] = useState(() => startUtc ? secondsUntilDoor(startUtc) : -1);
  const ref = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (!startUtc) return;
    setSecs(secondsUntilDoor(startUtc));
    ref.current = setInterval(() => {
      const remaining = secondsUntilDoor(startUtc);
      setSecs(remaining);
      if (remaining <= -60 * 60) {
        // Call ended > 1hr ago — stop ticking
        if (ref.current) clearInterval(ref.current);
      }
    }, 1000);
    return () => { if (ref.current) clearInterval(ref.current); };
  }, [startUtc]);

  return secs;
}

/** Registration form for registration-gated calls (town halls). Two steps,
 *  matching the standard registration flow: email first (the seat is saved the
 *  moment it lands), then first + last name. Remembers a completed registration
 *  in localStorage so revisits show the saved state. */
/** The door's sentence about the recording when sign-ups do not get it.
 *  Pinned word for word by worker/src/calls-source-flags.test.ts. */
const RECORDING_MEMBERS_LINE = "Can't make it live? The recording goes to Member Plus and Pro members afterward.";

/** The door's sentence while sign-ups get the recording free for a few days
 *  (worker recordingWindowLine, pinned by calls-source-flags.test.ts). */
function recordingWindowLine(days: number): string {
  return `Can't make it live? Register and the recording is yours free for ${days} ${days === 1 ? 'day' : 'days'} after the replay is released, then it moves to Member Plus and Pro.`;
}

/** The recording sentence for a call whose sign-up does not simply get the
 *  recording; null keeps the "everyone" wording. */
function recordingLineFor(call: Pick<CallData, 'recording_for' | 'recording_window_days'>): string | null {
  if (call.recording_for === 'members') return RECORDING_MEMBERS_LINE;
  if (call.recording_for === 'window' && call.recording_window_days) return recordingWindowLine(call.recording_window_days);
  return null;
}

function RsvpCard({ slug, recordingLine }: { slug: string; recordingLine: string | null }) {
  const storageKey = `call-rsvp:${slug}`;
  const navigate = useNavigate();
  const [done] = useState<boolean>(() => {
    try { return !!window.localStorage.getItem(storageKey); } catch { return false; }
  });
  const [step, setStep] = useState<'email' | 'names'>('email');
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [country, setCountry] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function post(body: Record<string, string>): Promise<{ ok?: boolean; already?: boolean; error?: string }> {
    const r = await fetch(`${API_BASE}/api/public/calls/${encodeURIComponent(slug)}/rsvp`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...body, attribution: getAttributionPayload() }),
    });
    const d = await r.json().catch(() => ({})) as { ok?: boolean; already?: boolean; error?: string };
    if (!r.ok || !d.ok) throw new Error(d.error ?? 'Could not save your registration — please try again.');
    return d;
  }

  function finish() {
    try { window.localStorage.setItem(storageKey, new Date().toISOString()); } catch { /* private mode */ }
    // Carried to the thank-you page for its Weekly Close box; cleared with the tab.
    try { window.sessionStorage.setItem(`call-rsvp-email:${slug}`, email.trim()); } catch { /* private mode */ }
    navigate(`/calls/${encodeURIComponent(slug)}/registered`);
  }

  async function submitEmail(e: FormEvent) {
    e.preventDefault();
    if (busy) return;
    setError(null);
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      setError('Please enter a valid email address.');
      return;
    }
    setBusy(true);
    try {
      const d = await post({ email: email.trim(), stage: 'email' });
      if (d.already) { finish(); return; } // registered before — no need to re-ask names
      setStep('names');
      setBusy(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setBusy(false);
    }
  }

  async function submitNames(e: FormEvent) {
    e.preventDefault();
    if (busy) return;
    setError(null);
    if (!firstName.trim()) { setError('Please enter your first name.'); return; }
    if (!lastName.trim()) { setError('Please enter your last name.'); return; }
    setBusy(true);
    try {
      await post({ email: email.trim(), first_name: firstName.trim(), last_name: lastName.trim(), ...(country ? { country } : {}) });
      finish();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setBusy(false);
    }
  }

  const fieldStyle: CSSProperties = {
    width: '100%', boxSizing: 'border-box', padding: '13px 16px', borderRadius: 8,
    border: '1px solid rgba(255,255,255,0.25)', background: 'rgba(255,255,255,0.95)',
    fontSize: 15, fontFamily: "'Poppins', sans-serif", color: '#0A0A2E',
  };

  if (done) {
    return (
      <div style={{
        maxWidth: 420, width: '100%', background: 'rgba(74,222,128,0.12)',
        border: '1px solid rgba(74,222,128,0.4)', borderRadius: 14, padding: '22px 26px',
      }}>
        <div style={{ fontSize: 16, fontWeight: 700, color: '#4ade80', marginBottom: 6 }}>
          You're registered — seat saved.
        </div>
        <div style={{ fontSize: 13, color: '#c5ccd6', lineHeight: 1.6 }}>
          Check your inbox for a confirmation with a calendar invite (it lands in your
          time zone automatically). {recordingLine
            ? <>The Zoom button appears right here before the call. {recordingLine}</>
            : <>The Zoom button appears right here before the call,
          and we'll send everyone the recording afterward — so you're covered either way.</>}
        </div>
      </div>
    );
  }

  const buttonStyle: CSSProperties = {
    background: '#F05A28', color: '#fff', border: 'none', cursor: busy ? 'wait' : 'pointer',
    padding: '14px 20px', borderRadius: 8, fontSize: 15, fontWeight: 700,
    fontFamily: "'Poppins', sans-serif", letterSpacing: 0.2, opacity: busy ? 0.7 : 1,
  };
  const cardStyle: CSSProperties = {
    maxWidth: 420, width: '100%', background: 'rgba(255,255,255,0.07)',
    border: '1px solid rgba(255,255,255,0.16)', borderRadius: 14, padding: '24px 26px',
    display: 'flex', flexDirection: 'column', gap: 10,
  };

  if (step === 'names') {
    return (
      <div style={{ maxWidth: 420, width: '100%' }}>
        <form onSubmit={submitNames} style={cardStyle}>
          <div style={{ fontSize: 15, fontWeight: 700, color: '#fff', marginBottom: 2 }}>
            Seat saved — who's joining?
          </div>
          <input type="text" placeholder="First name" value={firstName} required autoComplete="given-name"
            autoFocus onChange={(e) => setFirstName(e.target.value)} style={fieldStyle} />
          <input type="text" placeholder="Last name" value={lastName} required autoComplete="family-name"
            onChange={(e) => setLastName(e.target.value)} style={fieldStyle} />
          <CountrySelect value={country} onChange={setCountry} style={fieldStyle} />
          {error && <div style={{ fontSize: 13, color: '#fda4af' }}>{error}</div>}
          <button type="submit" disabled={busy} className="cta-btn" style={buttonStyle}>
            {busy ? 'Saving…' : 'Complete registration'}
          </button>
          <div style={{ fontSize: 12, color: '#9ca3af', lineHeight: 1.5 }}>
            Your confirmation email and calendar invite arrive as soon as you finish.
          </div>
        </form>
        <SmsOptIn
          email={email.trim() || null}
          firstName={firstName.trim() || null}
          lastName={lastName.trim() || null}
          source="call-rsvp"
          sourceDetail={slug}
          heading="Want a text reminder before the call?"
        />
        {offersNewsletterOnCall(slug) && (
          <NewsletterOptIn
            email={email.trim() || null}
            firstName={firstName.trim() || null}
            lastName={lastName.trim() || null}
            source="call-rsvp"
            sourceDetail={slug}
            page="sign-up"
            style={{ color: '#d1d5db' }}
          />
        )}
      </div>
    );
  }

  return (
    <form onSubmit={submitEmail} style={cardStyle}>
      <div style={{ fontSize: 15, fontWeight: 700, color: '#fff', marginBottom: 2 }}>Save your seat</div>
      <input type="email" placeholder="Email address" value={email} required autoComplete="email"
        onChange={(e) => setEmail(e.target.value)} style={fieldStyle} />
      {error && <div style={{ fontSize: 13, color: '#fda4af' }}>{error}</div>}
      <button type="submit" disabled={busy} className="cta-btn" style={buttonStyle}>
        {busy ? 'Saving…' : 'Register for the call'}
      </button>
      <div style={{ fontSize: 12, color: '#9ca3af', lineHeight: 1.5 }}>
        You'll get a confirmation with a calendar invite, plus reminders before the call.
        {recordingLine ? ` ${recordingLine}` : " Can't make it live? Register anyway — we'll send everyone the recording afterward."}
      </div>
    </form>
  );
}

export default function CallLandingPage() {
  const { callSlug } = useParams<{ callSlug: string }>();
  const [state, setState] = useState<PageState>({ phase: 'loading' });
  const navigate = useNavigate();
  const [retryNonce, setRetryNonce] = useState(0);

  // Landing-views beacon — fires once per mount for funnel tracking. Same
  // convention as WaitlistPage/LetsTalkPage/ReplayPage/ProgramSalesPage; see
  // "Landing-views beacon coverage" in sop-ee-anon-visitor-attribution.md —
  // this page was missing it (fixed 2026-09-18), meaning call landing traffic
  // had zero pageview tracking from any source (GTM is deliberately silenced
  // on discover.thesuccessfulbookkeeper.com in favor of this beacon).
  useEffect(() => {
    if (callSlug) recordLandingView(callSlug);
  }, [callSlug]);

  // The personal reminder-link token (?at=) is read once, into memory, on the
  // first render. The effect below then takes it off the address bar, because
  // GTM and the Meta pixel send the page address to Google and Meta.
  const [at] = useState<string | null>(() => splitCallSearch(window.location.search).at);

  // Must stay ABOVE useBrandGtm(), and BELOW the landing-views beacon: effects
  // run in order, so the beacon reads the email reference (?ee_r=) first, then
  // the address is clean by the time the container loads.
  useEffect(() => {
    const { cleanSearch } = splitCallSearch(window.location.search);
    if (cleanSearch !== window.location.search) {
      window.history.replaceState(window.history.state, '', window.location.pathname + cleanSearch + window.location.hash);
    }
  }, []);

  // Brand container (GTM-TTZVPNB on the TSB host) so the Meta pixel sees call
  // landing visits like it does on event pages. See sop-ee-calls-runtime.
  useBrandGtm();

  useEffect(() => {
    if (!callSlug) { setState({ phase: 'error', message: 'No call specified.' }); return; }
    setState((prev) => (prev.phase === 'ready' ? prev : { phase: 'loading' }));
    fetchWithRetry(`${API_BASE}/api/public/calls/${encodeURIComponent(callSlug)}`)
      .then(async (r) => {
        if (!r.ok) throw new Error(r.status === 404 ? 'This call was not found.' : `Server error ${r.status}`);
        const d = await r.json() as { call?: CallData; next_occurrence?: NextOccurrence | null; upcoming_dates?: UpcomingDate[]; redirect_slug?: string };
        // A slug retired when Command Centre merged meetings: go to the surviving
        // door page, keeping any personal link token or UTMs on the address.
        if (d.redirect_slug && d.redirect_slug !== callSlug) {
          navigate(`/calls/${encodeURIComponent(d.redirect_slug)}${window.location.search}`, { replace: true });
          return;
        }
        if (!d.call) throw new Error('This call was not found.');
        setState({ phase: 'ready', call: d.call, next: d.next_occurrence ?? null, upcoming: d.upcoming_dates ?? [] });
      })
      .catch((err) => setState({ phase: 'error', message: err instanceof Error ? err.message : String(err) }));
  }, [callSlug, retryNonce, navigate]);

  const next = state.phase === 'ready' ? state.next : null;
  const countdown = useCountdown(next?.start_utc ?? null);
  const doorsOpen = countdown <= 0;
  // zoom_ready signals a link exists even before the window (the raw URL is
  // withheld until doors open); fall back to the URL for older payloads.
  const hasZoom = !!(next?.zoom_ready ?? next?.zoom_join_url);
  const joinUrl = hasZoom && doorsOpen
    ? `https://get.thesuccessfulbookkeeper.com/zoom/calls/${encodeURIComponent(state.phase === 'ready' ? state.call.slug : '')}`
    : null;

  // Attendance identity gate (Phase 1): if the visitor arrived on their personal
  // reminder link (?at=<token>), greet them and record verified attendance at the
  // door. Device memory greets returning visitors on the same device. Everything
  // here is a nicety layered over the plain join button — it never gates entry.
  const [identity, setIdentity] = useState<DoorIdentity | null>(null);
  const [notMe, setNotMe] = useState(false);

  // The email card resolved a member — greet them (confirm card) + remember the
  // device. The email-match attendance row was already recorded server-side.
  const handleRecognized = (id: DoorIdentity) => {
    setNotMe(false);
    setIdentity(id);
    try { localStorage.setItem(`call-checkin-id:${callSlug}`, JSON.stringify(id)); } catch { /* ignore */ }
  };

  useEffect(() => {
    if (!callSlug) return;
    try {
      const remembered = localStorage.getItem(`call-checkin-id:${callSlug}`);
      if (remembered) setIdentity(JSON.parse(remembered));
    } catch { /* ignore */ }
    if (!at) return;
    fetch(`${API_BASE}/api/public/calls/${encodeURIComponent(callSlug)}/identity?at=${encodeURIComponent(at)}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (d?.identity) setIdentity(d.identity); })
      .catch(() => { /* fail-open: stay anonymous */ });
  }, [callSlug, at]);

  // Fire-and-forget: record the door check-in + remember the device. Never
  // awaited — the Zoom tab opens natively via the anchor regardless.
  const recordCheckin = () => {
    if (!callSlug || !identity) return;
    try { localStorage.setItem(`call-checkin-id:${callSlug}`, JSON.stringify(identity)); } catch { /* ignore */ }
    if (!at) return; // no token → greeting only, attendance falls back to Zoom matching
    fetch(`${API_BASE}/api/public/calls/${encodeURIComponent(callSlug)}/checkin`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ at }), keepalive: true,
    }).catch(() => { /* the gate records; it never blocks entry */ });
  };

  if (state.phase === 'loading') {
    return (
      <TakerShell>
        <div style={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ color: '#6b7280', fontSize: 14 }}>Loading…</div>
        </div>
      </TakerShell>
    );
  }

  if (state.phase === 'error') {
    return (
      <TakerShell>
        <div style={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ color: '#991b1b', fontSize: 15, marginBottom: 16 }}>{state.message}</div>
            <button
              type="button"
              onClick={() => setRetryNonce((n) => n + 1)}
              style={{
                padding: '10px 24px',
                borderRadius: 8,
                border: 'none',
                background: '#F05A28',
                color: '#fff',
                fontSize: 15,
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              Try again
            </button>
          </div>
        </div>
      </TakerShell>
    );
  }

  const { call } = state;
  const title = call.display_name || call.name;
  const parts = next ? formatDateTime(next.start_utc, next.timezone, next.duration_minutes) : null;
  const rsvpMode = !!call.rsvp_open;
  const recordingLine = recordingLineFor(call);
  const inviteOnly = !!call.invite_only;
  const oneOff = !!call.one_off;
  const landing = call.landing ?? null;
  const audience = landing?.audience_label || call.audience_label || (inviteOnly ? 'invited members' : 'TSB members');
  // "Membership" only when the audience is a membership; BKOS licensees and
  // program participants signed up, they didn't join a plan. A one-off names
  // its guests itself, so it points at the invite.
  const emailHint = oneOff ? 'your invite came to'
    : /member/i.test(audience) ? 'your membership is under' : 'you signed up with';
  const visitorLocal = next ? formatVisitorLocal(next.start_utc, next.timezone) : null;
  const hostList: CallHost[] = call.hosts?.length
    ? call.hosts
    : call.host
      ? [{ name: call.host, tagline: call.host_tagline, photo_url: call.host_photo_url }]
      : [];

  // What to show in the join slot
  const joinSlot = (() => {
    if (!next) return null; // no upcoming occurrence — the hero falls back to date-free copy
    if (doorsOpen && !joinUrl) {
      // Doors are open but no room exists yet (provisioning late or failed).
      // The room will appear here — never point them at their inbox.
      return (
        <div style={{
          display: 'inline-block', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.14)',
          borderRadius: 12, padding: '14px 28px', fontSize: 14, color: '#e2e8f0',
        }}>
          Getting the room ready — refresh this page in a moment.
        </div>
      );
    }
    if (doorsOpen) {
      const joinBtnStyle: CSSProperties = {
        display: 'inline-block', background: '#F05A28', color: '#fff',
        padding: '14px 36px', borderRadius: 8, fontSize: 15, fontWeight: 700,
        textDecoration: 'none', letterSpacing: 0.2,
      };
      // Identity confirm — "You're joining as Jane". Tapping records verified
      // attendance (fire-and-forget) and opens Zoom via the native anchor.
      if (identity && !notMe) {
        const who = identity.firstName?.trim() || identity.email;
        return (
          <div style={{
            display: 'inline-flex', flexDirection: 'column', alignItems: 'center', gap: 10,
            background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.14)',
            borderRadius: 12, padding: '18px 28px', maxWidth: 360,
          }}>
            <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase' as const, color: '#9ca3af' }}>
              You're joining as
            </div>
            <div style={{ fontSize: 20, fontWeight: 800, color: '#fff' }}>{who}</div>
            <a href={joinUrl!} target="_blank" rel="noopener noreferrer"
              className="cta-btn" onClick={recordCheckin} style={joinBtnStyle}>
              Yes — enter the meeting
            </a>
            <button type="button" onClick={() => setNotMe(true)}
              style={{ background: 'none', border: 'none', color: '#9ca3af', fontSize: 12.5, cursor: 'pointer', textDecoration: 'underline', padding: 0 }}>
              Not you?
            </button>
          </div>
        );
      }
      // Unknown visitor (no token, no device memory, or "Not you?") — ask for the
      // membership email and resolve it (Phase 2). "Just join" preserves the
      // anonymous path, so nobody is ever blocked.
      return (
        <DoorEmailCard
          callSlug={call.slug}
          joinUrl={joinUrl!}
          joinBtnStyle={joinBtnStyle}
          onRecognized={handleRecognized}
        />
      );
    }
    // Countdown state — shown whether or not the Zoom room is provisioned yet
    // (the horizon is 14 days, so most between-call visits land here); the
    // member only needs to know when to come back.
    return (
      <div style={{
        display: 'inline-flex', flexDirection: 'column', alignItems: 'center', gap: 6,
        background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.14)',
        borderRadius: 12, padding: '16px 32px',
      }}>
        <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase' as const, color: '#9ca3af' }}>
          Doors open in
        </div>
        <div style={{ fontSize: 28, fontWeight: 800, color: '#F8A07A', fontVariantNumeric: 'tabular-nums', letterSpacing: 0.5 }}>
          {formatCountdown(countdown)}
        </div>
        <div style={{ fontSize: 12, color: '#6b7280' }}>
          {DOOR_OPEN_MINUTES} minutes before the call starts
        </div>
      </div>
    );
  })();

  return (
    <TakerShell
      ctaHref={joinUrl}
      ctaLabel="Join the Call"
      footerLabel={rsvpMode ? 'Free with registration' : `For ${audience}`}
    >
      {/* Hero */}
      <section style={{
        background: 'linear-gradient(160deg, #0A0A2E 0%, #14143d 100%)',
        color: '#fff', padding: '64px 32px 80px', textAlign: 'center',
        fontFamily: "'Poppins', sans-serif",
        position: 'relative', overflow: 'hidden',
      }}>
        <div style={{
          position: 'absolute', top: -120, right: -120, width: 400, height: 400,
          borderRadius: '50%', background: 'rgba(240,90,40,0.07)', pointerEvents: 'none',
        }} />
        <div style={{
          display: 'inline-block', background: 'rgba(240,90,40,0.18)', color: '#F8A07A',
          fontSize: 11, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase' as const,
          padding: '5px 16px', borderRadius: 999, marginBottom: 28,
        }}>
          {rsvpMode ? 'Live on Zoom · Registration open' : `${audience} Call`}
        </div>
        <h1 style={{
          fontFamily: "'Fraunces', serif",
          fontSize: 'clamp(34px, 5.5vw, 52px)', fontWeight: 900, lineHeight: 1.12,
          marginBottom: 18, maxWidth: 700, margin: '0 auto 18px',
        }}>
          {title}
        </h1>
        {call.tagline && (
          <p style={{ fontSize: 18, color: '#c5ccd6', maxWidth: 560, margin: `0 auto ${rsvpMode ? 36 : 14}px`, lineHeight: 1.65 }}>
            {call.tagline}
          </p>
        )}
        {!rsvpMode && (
          <p style={{ fontSize: 14, color: '#F8A07A', maxWidth: 560, margin: '0 auto 30px', lineHeight: 1.6 }}>
            For {audience} &mdash; use the email {emailHint}.
          </p>
        )}

        {parts && (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16 }}>
            <div style={{
              display: 'inline-flex', alignItems: 'center', gap: 12,
              background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.14)',
              borderRadius: 10, padding: '12px 24px', fontSize: 14, color: '#e2e8f0',
            }}>
              <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#4ade80', flexShrink: 0, boxShadow: '0 0 6px #4ade80' }} />
              <span>
                <strong style={{ color: '#fff' }}>{parts.dayLong}</strong>
                {' '}&middot;{' '}{parts.timeShort}
                {' '}&middot;{' '}{next!.duration_minutes} min
              </span>
            </div>
            {visitorLocal && (
              <div style={{ fontSize: 13, color: '#9ca3af', marginTop: -6 }}>
                For you, that's <strong style={{ color: '#e2e8f0' }}>{visitorLocal}</strong>
              </div>
            )}
            {rsvpMode && <RsvpCard slug={call.slug} recordingLine={recordingLine} />}
            {joinSlot}
          </div>
        )}
        {!parts && rsvpMode && (
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <RsvpCard slug={call.slug} recordingLine={recordingLine} />
          </div>
        )}
        {!parts && (
          <p style={{ color: '#9ca3af', fontSize: 14 }}>The next date will appear here as soon as it's scheduled — your reminder email brings you back to this page.</p>
        )}
      </section>

      {/* Body */}
      <div style={{ maxWidth: 860, margin: '0 auto', padding: '56px 24px 80px', fontFamily: "'Poppins', sans-serif" }}>

        {/* Question box — renders only when the call is switched on (or in preview) for it */}
        <CallPageAskBox callSlug={call.slug} at={at} />

        {/* Approved landing narrative — story + agenda (EE-owned, calls.landing_json) */}
        {landing && (landing.why_lead || landing.why_highlight?.length) && (
          <div style={{ background: '#fff', border: '1px solid #e8e3de', borderRadius: 16, padding: '32px 28px', marginBottom: 24, boxShadow: '0 2px 12px rgba(10,10,46,0.06)' }}>
            <h2 style={{ fontFamily: "'Poppins', sans-serif", fontSize: 24, fontWeight: 700, marginBottom: 14, color: '#0A0A2E' }}>
              {landing.why_title ?? 'Why this matters'}
            </h2>
            {landing.why_lead && (
              <p style={{ fontSize: 15, color: '#4b5563', lineHeight: 1.75, marginBottom: 16 }}>{landing.why_lead}</p>
            )}
            {!!landing.why_highlight?.length && (
              <div style={{ borderLeft: '4px solid #F05A28', background: '#FFF6F2', borderRadius: '0 12px 12px 0', padding: '20px 24px', marginBottom: 16 }}>
                {landing.why_highlight.map((p, i) => (
                  <p key={i} style={{ fontSize: 15, color: '#0A0A2E', lineHeight: 1.7, marginBottom: i === landing.why_highlight!.length - 1 ? 0 : 10 }}>{p}</p>
                ))}
              </div>
            )}
            {landing.why_close && (
              <p style={{ fontSize: 15, color: '#4b5563', lineHeight: 1.75, margin: 0 }}>{landing.why_close}</p>
            )}
          </div>
        )}

        {landing && !!landing.agenda?.length && (
          <div style={{ background: '#fff', border: '1px solid #e8e3de', borderRadius: 16, padding: '32px 28px', marginBottom: 44, boxShadow: '0 2px 12px rgba(10,10,46,0.06)' }}>
            <h2 style={{ fontFamily: "'Poppins', sans-serif", fontSize: 24, fontWeight: 700, marginBottom: 6, color: '#0A0A2E' }}>
              {landing.agenda_title ?? "What we'll cover"}
            </h2>
            {landing.agenda.map((item, i) => (
              <div key={i} style={{ display: 'flex', gap: 14, padding: '14px 0', borderBottom: i === landing.agenda!.length - 1 ? 'none' : '1px solid #f0ece7' }}>
                <div style={{
                  flex: 'none', width: 26, height: 26, borderRadius: '50%', background: '#FFE4D6',
                  color: '#F05A28', fontWeight: 700, fontSize: 13,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', marginTop: 2,
                }}>{i + 1}</div>
                <div style={{ fontSize: 15, lineHeight: 1.65, color: '#4b5563' }}>
                  <strong style={{ color: '#0A0A2E' }}>{item.title}</strong>{' '}{item.body}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Host cards — a call can have multiple hosts (e.g. a town hall) */}
        {hostList.length > 0 && (
          <div style={{ display: 'grid', gridTemplateColumns: hostList.length > 1 ? 'repeat(auto-fill, minmax(360px, 1fr))' : '1fr', gap: 18, marginBottom: 44 }}>
            {hostList.map((h) => (
              <div key={h.name} style={{
                background: '#fff', border: '1px solid #e8e3de', borderRadius: 16,
                padding: '32px 28px', display: 'flex', alignItems: 'flex-start', gap: 24,
                boxShadow: '0 2px 12px rgba(10,10,46,0.06)',
              }}>
                {h.photo_url ? (
                  <img src={h.photo_url} alt={h.name} style={{
                    width: 96, height: 96, borderRadius: '50%', objectFit: 'cover',
                    flexShrink: 0, border: '3px solid #F05A28',
                  }} />
                ) : (
                  <div style={{
                    width: 96, height: 96, borderRadius: '50%', flexShrink: 0,
                    background: 'linear-gradient(135deg,#F05A28,#0A0A2E)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    color: '#fff', fontSize: 30, fontWeight: 700,
                  }}>
                    {h.name.split(/\s+/).map((w) => w[0]).join('').slice(0, 2).toUpperCase()}
                  </div>
                )}
                <div>
                  <h2 style={{ fontFamily: "'Poppins', sans-serif", fontSize: 22, fontWeight: 700, marginBottom: 4, color: '#0A0A2E' }}>
                    {h.name}
                  </h2>
                  {/* The host's real title from People, or nothing — never an
                      invented fallback title (mct_6baf1725-e1b). */}
                  {(h.role || h.tagline) ? (
                    <div style={{ fontSize: 12, fontWeight: 700, color: '#F05A28', letterSpacing: 0.5, textTransform: 'uppercase' as const, marginBottom: h.role && h.tagline && !textsOverlap(h.role, h.tagline) ? 4 : 12 }}>
                      {h.role || h.tagline}
                    </div>
                  ) : <div style={{ marginBottom: 8 }} />}
                  {h.role && h.tagline && !textsOverlap(h.role, h.tagline) && (
                    <div style={{ fontSize: 13, fontStyle: 'italic' as const, color: '#6b7280', marginBottom: 12 }}>
                      {h.tagline}
                    </div>
                  )}
                  {hostList.length === 1 && (
                    <p style={{ fontSize: 15, color: '#4b5563', lineHeight: 1.7, maxWidth: 580 }}>
                      {call.blurb ?? call.description ?? `${h.name} works with TSB bookkeepers at every stage — bringing real-world expertise to every call.`}
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* W5 grid */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(360px, 1fr))', gap: 18, marginBottom: 20 }}>
          {[
            rsvpMode || oneOff
              ? { label: 'What', title: 'A live session on Zoom', body: call.description ?? 'A live, all-in session — the full story, shown and told directly, with your questions answered on the spot.' }
              : { label: 'What', title: 'A live coaching call', body: 'Small-group, direct, and real. Bring the situations you\'re working through — your clients, your pricing conversations, your bottlenecks. Not theory. The actual work.' },
            { label: 'When', title: callWhenTitle(call.cadence, call.series_end_date, state.upcoming), body: parts ? <span><strong style={{ color: '#0A0A2E' }}>Next: {parts.dayLong}, {parts.timeRange}.</strong>{' '}{rsvpMode ? 'Register and we\'ll send you a calendar invite plus reminders before the call — no calendar hunting.' : oneOff ? 'You\'ll get a reminder before it starts — no calendar hunting.' : 'You\'ll get a reminder 1 week, 1 day, and 1 hour before each call — no calendar hunting.'}</span> : 'The next date appears on this page as soon as it\'s scheduled.' },
            { label: 'Where', title: 'Private Zoom', body: 'The join link opens 15 minutes before the call. Come back to this page when it\'s time and the button will be here waiting.' },
            rsvpMode
              ? { label: 'Who', title: 'Open with registration', body: recordingLine
                  ? `Open to every bookkeeping business owner. Register free and we'll send your calendar invite and reminders. ${recordingLine}`
                  : 'Open to every bookkeeping business owner. Register free and we\'ll send your calendar invite, reminders and the recording.' }
              : { label: 'Who', title: audience, body: `This ${oneOff ? 'session' : 'call'} is for ${audience}. If that's you, you're already on the list — nothing extra to register for.` },
          ].map(({ label, title: cardTitle, body }) => (
            <div key={label} style={{ background: '#fff', border: '1px solid #e8e3de', borderRadius: 12, padding: '24px 22px', boxShadow: '0 1px 6px rgba(10,10,46,0.04)' }}>
              <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase' as const, color: '#F05A28', marginBottom: 8 }}>{label}</div>
              <h3 style={{ fontFamily: "'Poppins', sans-serif", fontSize: 17, fontWeight: 700, marginBottom: 8, color: '#0A0A2E' }}>{cardTitle}</h3>
              <div style={{ fontSize: 14, color: '#6b7280', lineHeight: 1.65 }}>{body}</div>
            </div>
          ))}
        </div>

        {/* Closing card (e.g. "After the town hall: the first cohort") */}
        {landing && landing.closing_body && (
          <div style={{ background: 'linear-gradient(135deg,#0A0A2E 0%,#14143d 100%)', borderRadius: 16, padding: '36px 32px', marginBottom: 44, color: '#fff' }}>
            {landing.closing_title && (
              <h3 style={{ fontFamily: "'Poppins', sans-serif", fontSize: 21, fontWeight: 700, marginBottom: 10, color: '#F8A07A' }}>
                {landing.closing_title}
              </h3>
            )}
            <p style={{ fontSize: 15, color: '#d8d8e8', lineHeight: 1.7, margin: 0 }}>{landing.closing_body}</p>
          </div>
        )}

        {/* Why it matters — coaching-call framing; hidden for registration calls and one-offs */}
        {!rsvpMode && !oneOff && (
        <div style={{ background: '#fff', border: '1px solid #e8e3de', borderRadius: 14, padding: '30px 28px', marginBottom: 44, boxShadow: '0 1px 6px rgba(10,10,46,0.04)' }}>
          <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase' as const, color: '#F05A28', marginBottom: 8 }}>Why it matters</div>
          <h3 style={{ fontFamily: "'Poppins', sans-serif", fontSize: 20, fontWeight: 700, marginBottom: 10, color: '#0A0A2E' }}>Where the real transformation happens</h3>
          <p style={{ fontSize: 14, color: '#6b7280', lineHeight: 1.75 }}>
            The program gives you the framework. This call is where you apply it — out loud, with someone who has seen your situation before. The bookkeepers who show up consistently are the ones who move fastest. Bring a question, a current client situation, or a win. Leave with a specific next step.
          </p>
        </div>
        )}

        {/* CTA block */}
        <div style={{ background: 'linear-gradient(135deg,#0A0A2E 0%,#14143d 100%)', borderRadius: 16, padding: '44px 36px', textAlign: 'center', color: '#fff' }}>
          <h2 style={{ fontFamily: "'Poppins', sans-serif", fontSize: 28, fontWeight: 900, marginBottom: 12, lineHeight: 1.2 }}>
            Ready to join?
          </h2>
          <p style={{ color: '#bdc3cc', fontSize: 15, marginBottom: 28, maxWidth: 480, margin: '0 auto 28px', lineHeight: 1.65 }}>
            {/* This page IS the door: the reminder email links back here and never
                carries a join link, so the closed state must say "come back here",
                not "check your email" (that copy stranded a member, 2026-09-25). */}
            {parts
              ? (doorsOpen
                  ? `Doors are open for today's call (${parts.timeShort}) — join from right here.`
                  : `The next call is ${parts.dayLong} at ${parts.timeShort}. Come back to this page ${DOOR_OPEN_MINUTES} minutes before it starts — the Join button appears right here.`)
              : 'The next date will appear here as soon as it\'s scheduled. Your reminder email always brings you back to this page.'}
          </p>
          {joinUrl ? (
            <a href={joinUrl} target="_blank" rel="noopener noreferrer"
              className="cta-btn"
              style={{ display: 'inline-block', background: '#F05A28', color: '#fff', padding: '15px 44px', borderRadius: 8, fontSize: 16, fontWeight: 700, textDecoration: 'none', letterSpacing: 0.2 }}>
              Join on Zoom
            </a>
          ) : parts && !doorsOpen ? (
            <div style={{ display: 'inline-block', background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.18)', borderRadius: 8, padding: '12px 24px', fontSize: 14, color: '#e2e8f0' }}>
              Join button opens here {DOOR_OPEN_MINUTES} min before the call
            </div>
          ) : parts ? (
            <div style={{ display: 'inline-block', background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.18)', borderRadius: 8, padding: '12px 24px', fontSize: 14, color: '#e2e8f0' }}>
              Getting the room ready — refresh this page in a moment
            </div>
          ) : (
            <div style={{ display: 'inline-block', background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.18)', borderRadius: 8, padding: '12px 24px', fontSize: 14, color: '#e2e8f0' }}>
              Next date coming soon — check back here
            </div>
          )}
        </div>

        {/* Non-member CTA — membership framing; hidden for registration calls and
            one-offs ("your place on the next call" has no next call to offer) */}
        {!rsvpMode && !inviteOnly && !oneOff && (
        <div style={{
          marginTop: 20, background: '#fff', border: '1px solid #e8e3de', borderRadius: 14,
          padding: '32px 28px', textAlign: 'center', boxShadow: '0 1px 6px rgba(10,10,46,0.04)',
        }}>
          <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase' as const, color: '#F05A28', marginBottom: 8 }}>
            Not a member yet?
          </div>
          <h3 style={{ fontFamily: "'Poppins', sans-serif", fontSize: 20, fontWeight: 700, marginBottom: 10, color: '#0A0A2E' }}>
            This call is for {audience}
          </h3>
          <p style={{ fontSize: 14, color: '#6b7280', lineHeight: 1.7, maxWidth: 540, margin: '0 auto 22px' }}>
            Live coaching calls like this one are reserved for {audience}.{' '}
            {call.register_url
              ? 'Register here and your place on the next call follows as soon as your membership is active.'
              : "If you'd like to be part of it, start by exploring what TSB offers."}
          </p>
          {call.register_url ? (
            <>
              <a href={call.register_url} className="cta-btn"
                style={{
                  display: 'inline-block', background: '#F05A28', color: '#fff',
                  padding: '13px 32px', borderRadius: 8, fontSize: 14, fontWeight: 700,
                  textDecoration: 'none', letterSpacing: 0.2,
                }}>
                Register here
              </a>
              {call.register_url_annual && (
                <p style={{ fontSize: 13, color: '#6b7280', margin: '14px 0 0' }}>
                  Prefer to pay once a year?{' '}
                  <a href={call.register_url_annual} style={{ color: '#F05A28', fontWeight: 600 }}>See the yearly plan</a>.
                </p>
              )}
            </>
          ) : (
            <a href="https://www.thesuccessfulbookkeeper.com" target="_blank" rel="noopener noreferrer"
              style={{
                display: 'inline-block', background: '#0A0A2E', color: '#fff',
                padding: '13px 32px', borderRadius: 8, fontSize: 14, fontWeight: 700,
                textDecoration: 'none', letterSpacing: 0.2,
              }}>
              Explore TSB
            </a>
          )}
        </div>
        )}

      </div>
    </TakerShell>
  );
}
